/* Pocket Factory · P1-FINISH-1, the card's vinyl finish as a material. assets/pf-card-material.css draws the finish on
   every card as a coat (.pc-card::after); this script moves it as the card really turns, so Glossy's reflection glides
   across the card with the tilt, the pointer and a drag, and Matte's soft light barely shifts.
   - Each card's orientation is read from the transforms the page puts on it and its scene: the hero's tilt and the
     pointer's (assets/pocket-cro.js), the editor's turn to the back (assets/pf-exp-editor.css), the gallery's scenes, a
     flat thumbnail. The product of their linear parts gives the card's axes U and V and its normal N in view space
     (x right, y down, z toward the viewer).
   - The studio's key light L is up and to the left, in front; the viewer looks straight in (V). A smooth surface mirrors
     the studio, its reflection centred where the half vector H = norm(L + V) meets the normal: GAIN x (H - N) along U
     and V from the card's centre. The coat is drawn for a flat card facing the viewer and is moved by the difference,
     by its background position (a repaint only: moving it by a transform made the browser rebuild its layers each frame).
   - The coat's place is held as a Web Animations value on the card's ::after (no attribute changes: nothing for other
     scripts' observers or a session recording to process; only the coat is restyled), or else the card's own custom
     properties (--pfm-x, --pfm-y). It is computed only while something moves (a transform transition or
     animation on a card's scene, the pointer over it, a drag), only for the cards whose scene moved, and every second or
     third frame on a device that is already slow. At rest nothing runs.
   - On a touch screen the viewer's card (the hero stage) turns under a finger: a drag tilts it through the stage's own
     --pfh-tx / --pfh-ty, which the pointer sets on a desktop, and it eases back on release. Not with reduced motion.
   - The tone (light, mid, dark or art) follows the colour the card is painted (its --card-color), a full-front design or
     the shopper's own back (data-pfm-tone, read by the stylesheet).
   window.PFCardMaterial.refresh() paints every card again (for a script that changes cards without a DOM change). */
(function (root, factory) {
  const api = factory();
  if (typeof module === 'object' && module.exports) module.exports = api;
  else { root.PFCardMaterial = api; api.start(root); }
})(typeof window !== 'undefined' ? window : globalThis, function () {
  'use strict';
  const norm = (v) => { const l = Math.hypot(v[0], v[1], v[2]) || 1; return [v[0] / l, v[1] / l, v[2] / l]; };
  const dot = (a, b) => a[0] * b[0] + a[1] * b[1] + a[2] * b[2];
  const cross = (a, b) => [a[1] * b[2] - a[2] * b[1], a[2] * b[0] - a[0] * b[2], a[0] * b[1] - a[1] * b[0]];
  const clamp = (x, lo, hi) => Math.min(hi, Math.max(lo, x));
  /** The studio's key light (up, left, in front) and the half vector between it and the viewer. */
  const LIGHT = norm([-0.36, -0.6, 0.72]);
  const HALF = norm([LIGHT[0], LIGHT[1], LIGHT[2] + 1]);
  /** Card widths the reflection travels per unit of (H - N); the coat is three cards wide, so it stops short of LIMIT. */
  const GAIN = 1.15;
  const LIMIT = 0.85;
  /** The card's long side over its short side (CR80). */
  const CR80 = 1.586;
  const IDENTITY = [1, 0, 0, 0, 1, 0, 0, 0, 1];

  /** A computed transform's linear part as a row-major 3x3, or null (none, or not a matrix). */
  function linear(text) {
    const m = /^(matrix3d|matrix)\(([^)]*)\)$/.exec(String(text || '').trim());
    if (!m) return null;
    const n = m[2].split(',').map(Number);
    if (n.some((x) => !Number.isFinite(x))) return null;
    // matrix(a, b, c, d, e, f): x' = a x + c y + e. matrix3d lists its columns: x' = n0 x + n4 y + n8 z + n12.
    if (m[1] === 'matrix') return n.length === 6 ? [n[0], n[2], 0, n[1], n[3], 0, 0, 0, 1] : null;
    return n.length === 16 ? [n[0], n[4], n[8], n[1], n[5], n[9], n[2], n[6], n[10]] : null;
  }
  const ANGLE = { deg: Math.PI / 180, rad: 1, grad: Math.PI / 200, turn: 2 * Math.PI };
  /** The CSS rotate property's computed value ("30deg", "x 30deg", "0 1 0 30deg") as a row-major 3x3, or null. */
  function rotation(text) {
    const parts = String(text || '').trim().split(/\s+/);
    const a = /^(-?[\d.]+(?:e-?\d+)?)(deg|rad|grad|turn)$/.exec(parts.pop() || '');
    if (!a) return null;
    const named = { x: [1, 0, 0], y: [0, 1, 0], z: [0, 0, 1] }[parts[0]];
    const axis = parts.length === 0 ? [0, 0, 1] : named || (parts.length === 3 ? parts.map(Number) : null);
    if (!axis || axis.some((x) => !Number.isFinite(x)) || !Math.hypot(...axis)) return null;
    const [x, y, z] = norm(axis), half = Number(a[1]) * ANGLE[a[2]] / 2, s = Math.sin(half), c = Math.cos(half), sq = s * s, sc = s * c;
    return [
      1 - 2 * (y * y + z * z) * sq, 2 * (x * y * sq - z * sc), 2 * (x * z * sq + y * sc),
      2 * (x * y * sq + z * sc), 1 - 2 * (x * x + z * z) * sq, 2 * (y * z * sq - x * sc),
      2 * (x * z * sq - y * sc), 2 * (y * z * sq + x * sc), 1 - 2 * (x * x + y * y) * sq,
    ];
  }
  function mul(a, b) {
    const o = new Array(9);
    for (let r = 0; r < 3; r++) for (let c = 0; c < 3; c++) o[r * 3 + c] = a[r * 3] * b[c] + a[r * 3 + 1] * b[3 + c] + a[r * 3 + 2] * b[6 + c];
    return o;
  }
  /** The card's unit axes U, V and normal N from the linear parts of its own transform and its scenes', the card's first. */
  function axes(parts) {
    let m = IDENTITY;
    for (const part of parts) if (part) m = mul(part, m);
    const u = norm([m[0], m[3], m[6]]), v = norm([m[1], m[4], m[7]]);
    return { u, v, n: norm(cross(u, v)) };
  }
  /**
   * Where the coat goes for a card with these axes: x and y its shift in card widths along the card's own axes. Zero for
   * a flat card facing the viewer, for which the coat is drawn.
   */
  function reflection(a) {
    const d = [HALF[0] - a.n[0], HALF[1] - a.n[1], HALF[2] - a.n[2]];
    let x = GAIN * (dot(d, a.u) - HALF[0]), y = GAIN * (dot(d, a.v) - HALF[1]);
    const far = Math.hypot(x, y);
    if (far > LIMIT) { x *= LIMIT / far; y *= LIMIT / far; }
    return { x, y };
  }
  /**
   * The coat's background position for that shift on a card whose width over height is aspect. The coat is drawn three
   * cards wide and high (background-size 300%), so 50% centres it, every 1% moves it a fiftieth of the card, and between
   * 0% and 100% it always covers the card: a card's height is shorter than its width, so a steep tilt stops sooner up
   * and down.
   */
  function coat(shift, aspect = CR80) {
    const pct = (value) => (Math.round(clamp(value, 0, 100) * 10) / 10 || 0) + '%';
    return { '--pfm-x': pct(50 - 50 * shift.x), '--pfm-y': pct(50 - 50 * shift.y * aspect) };
  }
  /**
   * light, mid or dark for a #rrggbb colour by its relative luminance; '' for anything else. Dark is the near-black
   * vinyl (a neutral dark, or any colour nearly black), whose glossy coat deepens it to piano black; a saturated dark
   * brand colour (navy, burgundy) is mid, so its own colour stays.
   */
  function tone(hex) {
    const m = /^#?([\da-f]{6})$/i.exec(String(hex || '').trim());
    if (!m) return '';
    const srgb = m[1].match(/../g).map((x) => parseInt(x, 16) / 255);
    const [r, g, b] = srgb.map((c) => (c <= 0.04045 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4));
    const y = 0.2126 * r + 0.7152 * g + 0.0722 * b, chroma = Math.max(...srgb) - Math.min(...srgb);
    return y > 0.5 ? 'light' : y < 0.012 || (y < 0.045 && chroma < 0.12) ? 'dark' : 'mid';
  }

  // ---------- In the browser ----------
  let started = false, api = null;
  function start(win) {
    const doc = win?.document;
    if (started || !doc?.documentElement) return;
    started = true;
    const go = () => { try { api = wire(win); } catch (_) {} };
    if (doc.readyState === 'loading') doc.addEventListener('DOMContentLoaded', go, { once: true });
    else go();
  }
  function refresh() { api?.refresh(); }

  const WATCH = ['data-finish', 'data-base-color', 'data-side', 'data-orientation', 'data-pf-brand-style', 'data-background-art', 'data-back-own', 'style'];
  const DEPTH = 10; // the card, its scene and their wrappers: every tilt the page draws is within these
  function wire(win) {
    const doc = win.document;
    // seen: cards on screen; hot: elements changed this frame (a card or a scene of one); fresh: cards just come into view.
    const cards = new Map(), seen = new Set(), written = new Set(), hot = new Set(), fresh = new Set();
    let frame = 0, until = 0, dirty = true, all = true;
    const now = () => win.performance?.now?.() ?? Date.now();
    const reduced = () => !!win.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

    const io = typeof win.IntersectionObserver === 'function'
      ? new win.IntersectionObserver((entries) => { for (const e of entries) { if (e.isIntersecting) { seen.add(e.target); fresh.add(e.target); } else seen.delete(e.target); } kick(0); }, { rootMargin: '96px' })
      : null;
    /** New or removed cards, a card repainted (its finish, colour, side, shape), or a scene of one changed. */
    function observed(records) {
      if (!records.length) return;
      for (const r of records) {
        if (r.type === 'childList') {
          if (r.removedNodes.length) dirty = true;
          for (const node of r.addedNodes) if (node.nodeType === 1 && (node.matches('.pc-card') || node.querySelector('.pc-card'))) { dirty = true; break; }
        } else {
          if (cards.has(r.target)) cards.get(r.target).stale = true;
          hot.add(r.target);
        }
      }
      kick(0);
    }
    const mo = typeof win.MutationObserver === 'function' ? new win.MutationObserver(observed) : null;

    function track(card) {
      let state = cards.get(card);
      if (state) return state;
      state = { key: '', tone: null, stale: true, anim: null };
      cards.set(card, state);
      io ? io.observe(card) : seen.add(card);
      fresh.add(card);
      mo?.observe(card, { attributes: true, attributeFilter: WATCH });
      // Its scenes too: a tilt set without a transition (reduced motion, a stage's own --pfh-tx) turns it at once. The
      // body keeps its own watch (new cards), which another observe() on it would replace.
      for (let el = card.parentElement, depth = 1; mo && el && el !== doc.body && el !== doc.documentElement && depth < DEPTH; el = el.parentElement, depth++) mo.observe(el, { attributes: true });
      return state;
    }
    function scan() {
      dirty = false;
      for (const card of doc.querySelectorAll('.pc-card')) track(card);
      for (const card of cards.keys()) {
        if (card.isConnected) continue;
        cards.delete(card);
        seen.delete(card);
        io?.unobserve(card);
      }
      for (const stage of doc.querySelectorAll('[data-pfh-stage]')) drag(stage);
    }
    /** The card's own linear part and its scenes', up to DEPTH elements. */
    function partsOf(card) {
      const parts = [];
      for (let el = card, depth = 0; el && el.nodeType === 1 && depth < DEPTH; el = el.parentElement, depth++) {
        const cs = win.getComputedStyle(el), t = linear(cs.transform), r = cs.rotate && cs.rotate !== 'none' ? rotation(cs.rotate) : null;
        // An element's rotate property applies after its transform list.
        if (t || r) parts.push(r && t ? mul(r, t) : r || t);
      }
      return parts;
    }
    /** The card's tone, from the colour it is painted (cheap: no style is computed). */
    function toneOf(card) {
      if (card.hasAttribute('data-background-art') || card.hasAttribute('data-back-own')) return 'art';
      return tone(card.style.getPropertyValue('--card-color')) || ({ Dark: 'dark', Light: 'light' })[card.dataset.baseColor] || 'mid';
    }
    /** The coat's custom properties for the card as it is turned now. */
    function placeOf(card) {
      return coat(reflection(axes(partsOf(card))), card.dataset.orientation === 'Vertical' ? 1 / CR80 : CR80);
    }
    // Economy: when the device is already slow (frames longer than about 24 ms in a row), the coat is placed every second
    // frame, and every third below about 25 frames a second; the card itself keeps turning on the compositor.
    let last = 0, pace = 16, count = 0;
    function tick() {
      frame = 0;
      const t = now();
      if (last && t - last < 250) pace = pace * 0.8 + (t - last) * 0.2;
      else pace = 16;
      last = t;
      count++;
      if (dirty) scan();
      // A transition or animation whose end never came (its element removed, say) is let go after 4 s.
      for (const [el, names] of moving) {
        for (const [name, by] of names) if (by < t || !el.isConnected) names.delete(name);
        if (!names.size) moving.delete(el);
      }
      const every = all;
      all = false;
      const stride = pace > 40 ? 3 : pace > 24 ? 2 : 1;
      const due = every || count % stride === 0;
      // Everything is read first, then written: a write between two reads would make the browser restyle for the second.
      // Placed again: every card the first time and on refresh, then only cards on screen whose scene moved (a running
      // transform transition or animation, a drag, a changed attribute) or that just came into view. Off screen a card
      // keeps its place until it is seen again.
      const roots = [...hot, ...moving.keys()];
      if (held) roots.push(held);
      const out = [];
      for (const [card, state] of cards) {
        const shade = state.stale ? toneOf(card) : null;
        const props = every || fresh.has(card) || (due && seen.has(card) && roots.some((root) => root === card || root.contains(card))) ? placeOf(card) : null;
        if (shade !== null || props) out.push([card, state, shade, props]);
      }
      // A skipped frame keeps what changed for the next one.
      if (due) hot.clear();
      fresh.clear();
      for (const [card, state, shade, props] of out) {
        if (shade !== null) {
          state.stale = false;
          if (shade !== state.tone) { state.tone = shade; if (card.getAttribute('data-pfm-tone') !== shade) card.setAttribute('data-pfm-tone', shade); }
        }
        if (!props) continue;
        const key = props['--pfm-x'] + ' ' + props['--pfm-y'];
        if (key === state.key) continue;
        state.key = key;
        write(card, state, props);
      }
      // This script's own writes are not news to it (anyone else's still are).
      if (written.size) {
        if (mo) observed(mo.takeRecords().filter((r) => !(r.type === 'attributes' && r.attributeName === 'style' && written.has(r.target))));
        written.clear();
      }
      if (now() < until || moving.size || held || hot.size) frame = win.requestAnimationFrame(tick);
      else last = 0;
    }
    /**
     * Moves the coat: a held Web Animations value on the card's ::after where the browser has them, which changes no
     * attribute (nothing for the page's observers or a session recording to process) and restyles only the coat; else
     * the card's own custom properties (a style sheet rule changed every frame made the browser restyle far more).
     */
    let animates = null;
    function write(card, state, props) {
      const value = props['--pfm-x'] + ' ' + props['--pfm-y'];
      // Asked once, of a card that has the coat.
      if (animates === null && card.matches('[data-finish][data-side]')) {
        animates = false;
        try {
          const probe = card.animate([{ backgroundPosition: '12.5% 25%' }], { duration: 0, fill: 'forwards', pseudoElement: '::after' });
          animates = /^12\.5% 25%/.test(win.getComputedStyle(card, '::after').backgroundPosition);
          probe.cancel();
        } catch (_) { animates = false; }
      }
      if (animates) {
        try {
          if (state.anim && state.anim.playState !== 'idle') state.anim.effect.setKeyframes([{ backgroundPosition: value }]);
          else state.anim = card.animate([{ backgroundPosition: value }], { duration: 0, fill: 'forwards', pseudoElement: '::after' });
          return;
        } catch (_) { animates = false; }
      }
      for (const name in props) card.style.setProperty(name, props[name]);
      written.add(card);
    }
    /** Paints in the next frame and keeps painting for ms after it. */
    function kick(ms) {
      until = Math.max(until, now() + ms);
      if (!frame) frame = win.requestAnimationFrame(tick);
    }

    // ---------- What moves a card ----------
    // Transform transitions and animations on a card or its scene, until they end.
    const moving = new Map();
    const turns = (el, name) => {
      if (!el.getAnimations) return true;
      const anim = el.getAnimations().find((a) => a.animationName === name);
      try { return !anim || anim.effect.getKeyframes().some((k) => 'transform' in k || 'rotate' in k); } catch (_) { return true; }
    };
    function animated(event) {
      const el = event.target;
      if (event.pseudoElement || !el || el.nodeType !== 1) return;
      const transition = event.type.startsWith('transition');
      if (transition && !/^(transform|rotate|all)$/.test(event.propertyName)) return;
      if (!(el.matches('.pc-card') || el.querySelector('.pc-card'))) return;
      const key = el, name = transition ? 't:' + event.propertyName : 'a:' + event.animationName;
      if (/run|start/.test(event.type)) {
        if (!transition && !turns(el, event.animationName)) return;
        const names = moving.get(key) || new Map();
        names.set(name, now() + 4000);
        moving.set(key, names);
      } else if (/end|cancel/.test(event.type)) {
        moving.get(key)?.delete(name);
        if (!moving.get(key)?.size) moving.delete(key);
      }
      hot.add(el);
      kick(120);
    }
    for (const type of ['transitionrun', 'transitionend', 'transitioncancel', 'animationstart', 'animationend', 'animationcancel']) doc.addEventListener(type, animated, true);
    // The pointer over a card's scene (the desktop tilt follows it), a design change, a resize.
    doc.addEventListener('pointermove', (event) => {
      const scene = event.target?.closest?.('[data-pfh-stage],.pf-product-scene,.pc-scene');
      if (scene) { hot.add(scene); kick(160); }
    }, { passive: true });
    const restyle = () => { for (const state of cards.values()) state.stale = true; kick(0); };
    for (const type of ['input', 'change', 'pocket:card-config', 'pocket:design-change', 'pocket:preview-hydrated']) doc.addEventListener(type, restyle);
    win.addEventListener('resize', () => { all = true; kick(0); }, { passive: true });
    mo?.observe(doc.body || doc.documentElement, { childList: true, subtree: true });

    // ---------- Drag to turn, on a touch screen ----------
    let held = null;
    const hoverTilt = win.matchMedia?.('(hover: hover) and (pointer: fine)');
    function drag(stage) {
      if (stage.hasAttribute('data-pfm-drag') || hoverTilt?.matches) return;
      stage.setAttribute('data-pfm-drag', '');
      let id = null, x0 = 0, y0 = 0, w = 0, h = 0;
      // The stage's size, kept as it changes, so a touch never waits on a layout.
      if (typeof win.ResizeObserver === 'function') new win.ResizeObserver((entries) => { const box = entries[entries.length - 1].contentRect; w = box.width; h = box.height; }).observe(stage);
      const set = (tx, ty) => {
        if (tx === null) { stage.style.removeProperty('--pfh-tx'); stage.style.removeProperty('--pfh-ty'); return; }
        stage.style.setProperty('--pfh-tx', tx.toFixed(2) + 'deg');
        stage.style.setProperty('--pfh-ty', ty.toFixed(2) + 'deg');
      };
      stage.addEventListener('pointerdown', (event) => {
        if (event.pointerType === 'mouse' || hoverTilt?.matches || reduced() || id !== null) return;
        id = event.pointerId;
        x0 = event.clientX;
        y0 = event.clientY;
        if (!w || !h) { const box = stage.getBoundingClientRect(); w = box.width; h = box.height; }
      }, { passive: true });
      stage.addEventListener('pointermove', (event) => {
        if (event.pointerId !== id) return;
        held = stage;
        // A finger dragged across the stage's width turns the card about 22 degrees, its height about 14.
        set(clamp((event.clientX - x0) / (w || 1) * 44, -22, 22), clamp(-(event.clientY - y0) / (h || 1) * 28, -14, 14));
        kick(500);
      }, { passive: true });
      const end = (event) => {
        if (event.pointerId !== id) return;
        id = null;
        held = null;
        set(null);
        kick(700);
      };
      stage.addEventListener('pointerup', end);
      stage.addEventListener('pointercancel', end);
      stage.addEventListener('lostpointercapture', end);
    }

    kick(0);
    return { refresh: () => { dirty = true; all = true; restyle(); } };
  }

  return { LIGHT, HALF, GAIN, LIMIT, linear, rotation, mul, axes, reflection, coat, tone, start, refresh };
});
